/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { useFormik } from 'formik';
import humanize from 'humanize-plus';
import { TOptions } from 'i18next';
import { DateTime } from 'luxon';
import { memo, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import VisibilityOffIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityIcon from '@mui/icons-material/VisibilityRounded';
import {
  Alert,
  AutocompleteRenderInputParams,
  Fade,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';

import { CIColors } from 'shared';

import { HUModal2, HUModal2Props } from '../../../../components/HUModal2/HUModal2';
import { notifyReportableApiError } from '../../../../components/notifyReportableApiError';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../elements/HUTextInput/utils';
import { ClusterApiService } from '../../../../services/ClusterApiService';
import { useCreateEntityOverlayFormStyles } from '../../../../styles/createEntityOverlay';
import { useFormStyles } from '../../../../styles/form';
import { useMaterialPickersStyles } from '../../../../styles/materialPickers';
import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { getDateFormatByLocale } from '../../../../utils/getDateFormatByLocale';
import { fetchClusterContinuousBackups } from '../../../../utils/queryFns';
import { useObjectState } from '../../../../utils/useObjectState';
import { useUtcFormat } from '../../../../utils/useUtcFormat';
import { restoreClusterInitialValues, restoreClusterValidationSchema } from './RestoreClusterDialog.consts';
import { useRestoreClusterDialogStyles } from './RestoreClusterDialog.styles';
import { RestoreClusterDataForm } from './RestoreClusterDialog.types';

type RestoreClusterDialogProps = Pick<HUModal2Props, 'open'> &
  Required<Pick<HUModal2Props, 'onCancel'>> & {
    identificationData: UpdateClusterDetailsParameterIdentification;
    storageAvailable: number;
    oldestRestorePoint: string | undefined;
  };

type SelectedBackupState = {
  isSelected: boolean;
  localTimestamp: string | null;
  showMore: boolean;
};

export const RestoreClusterDialog = memo<RestoreClusterDialogProps>(props => {
  const { open, onCancel, identificationData, storageAvailable, oldestRestorePoint } = props;
  const SHOW_MORE_OPTION = '__show_more__';

  const { t, i18n } = useTranslation();

  const classes = useCreateEntityOverlayFormStyles();
  const formClasses = useFormStyles();
  const mpClasses = useMaterialPickersStyles();
  const restoreClasses = useRestoreClusterDialogStyles();

  const { utcLocal, utcServer } = useUtcFormat();
  const [isFetching, setIsFetching] = useState(false);
  const initialSelectedBackupState: SelectedBackupState = {
    isSelected: false,
    localTimestamp: null,
    showMore: false,
  };
  const [selectedBackupState, setSelectedBackupState] = useObjectState<SelectedBackupState>({
    isSelected: false,
    localTimestamp: null,
    showMore: false,
  });

  const dateFormat = useMemo(() => getDateFormatByLocale(i18n.language), [i18n.language]);

  const clusterContinuousBackupsQuery = useQuery(
    [fetchClusterContinuousBackups.key, identificationData],
    fetchClusterContinuousBackups.fn,
    { retry: true, enabled: open, refetchOnMount: 'always' },
  );

  const continuousBackups = useMemo(
    () => clusterContinuousBackupsQuery.data?.data.payload || [],
    [clusterContinuousBackupsQuery.data],
  );

  const onFormSubmit = async (values: RestoreClusterDataForm) => {
    const backup = continuousBackups.find(b => b.name === values.backupName);

    if (backup) {
      const storageAvailableBytes = storageAvailable * Math.pow(1024, 2);
      if (backup.sizeInBytes > storageAvailableBytes) {
        setFieldError('backupName', 'insufficient_storage_space');
        return;
      }
    }

    const backupTimestamp = backup
      ? DateTime.fromISO(backup.lastModified, { zone: 'utc' }).plus({ seconds: 5 }).toISO()
      : values.backupTimestamp!.toISOString();

    if (backupTimestamp) {
      try {
        setIsFetching(true);
        await ClusterApiService.restoreClusterFromBackup(identificationData, { timestamp: backupTimestamp });
      } catch (error) {
        notifyReportableApiError(error).catch(() => console.error('Failed to notify reportable api:', error));
      } finally {
        setIsFetching(false);
        onClose();
      }
    }
  };

  const { values, errors, setFieldValue, setFieldError, submitForm, resetForm } = useFormik<RestoreClusterDataForm>({
    initialValues: restoreClusterInitialValues,
    validationSchema: restoreClusterValidationSchema,
    onSubmit: onFormSubmit,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const backupMaxTimestamp = useMemo(
    () => DateTime.fromJSDate(values.backupMaxTimestamp, { zone: 'utc' }),
    [values.backupMaxTimestamp],
  );

  const backupMinTimestamp = useMemo(
    () => DateTime.fromISO(oldestRestorePoint || '1900-01-01').toUTC(),
    [oldestRestorePoint],
  );

  const backupTimestamp = useMemo(
    () => values.backupTimestamp && DateTime.fromJSDate(values.backupTimestamp, { zone: 'utc' }),
    [values.backupTimestamp],
  );

  const hasError = (field: keyof RestoreClusterDataForm): boolean => {
    return !!errors[field];
  };

  const getError = (field: keyof RestoreClusterDataForm, options?: TOptions): string => {
    const error = errors[field];
    if (error) {
      return t(`createCluster:validation.${field}.${error}`, options);
    }
    return '';
  };

  useEffect(() => {
    if (open) {
      const now = DateTime.utc().toJSDate();
      void setFieldValue('backupTimestamp', now, false);
      void setFieldValue('backupMaxTimestamp', now, false);
    }
  }, [open, setFieldValue]);

  useEffect(() => {
    void setFieldValue('backupMinTimestamp', backupMinTimestamp.toJSDate(), false);
  }, [backupMinTimestamp, setFieldValue]);

  useEffect(() => {
    const delay = !values.backupName ? 500 : 0;
    const timer = setTimeout(() => {
      let timestamp = '';

      if (values.backupName) {
        timestamp = continuousBackups.find(b => b.name === values.backupName)?.lastModified || '';
      }

      setSelectedBackupState({
        localTimestamp: timestamp ? `${DateTime.fromISO(timestamp).toFormat(dateFormat)} (${utcLocal})` : null,
      });
    }, delay);

    return () => clearTimeout(timer);
  }, [values.backupName, continuousBackups, dateFormat, utcLocal, setSelectedBackupState]);

  const backupTimestampErrorOptions = useMemo(
    () => ({
      maxDate: backupMaxTimestamp.toFormat(dateFormat),
      minDate: backupMinTimestamp.toFormat(dateFormat),
      utc: utcServer,
    }),
    [backupMinTimestamp, dateFormat, backupMaxTimestamp, utcServer],
  );

  const handleBackupDateTimeChange = (value: DateTime | null) => {
    if (!value) {
      void setFieldValue('backupTimestamp', null);
      return;
    }

    if (value.toFormat(dateFormat) === backupMinTimestamp.toFormat(dateFormat)) {
      void setFieldValue('backupTimestamp', backupMinTimestamp.toJSDate());
    } else if (value.toFormat(dateFormat) === backupMaxTimestamp.toFormat(dateFormat)) {
      void setFieldValue('backupTimestamp', backupMaxTimestamp.toJSDate());
    } else {
      void setFieldValue('backupTimestamp', value.toJSDate());
    }
  };

  const continuousBackupsOptions = useMemo(() => {
    const options: string[] = [];

    const parseTimestamp = (timestamp: string) => DateTime.fromISO(timestamp, { zone: 'utc' });

    if (!backupTimestamp) {
      return options;
    }

    const backupsWithDateTimes = continuousBackups.map(backup => ({
      backup,
      dateTime: parseTimestamp(backup.lastModified),
    }));

    const sameDayBackups = backupsWithDateTimes
      .filter(b => b.dateTime.hasSame(backupTimestamp, 'day'))
      .sort((a, b) => b.dateTime.toMillis() - a.dateTime.toMillis());

    options.push(...sameDayBackups.map(b => b.backup.name));

    const cutoffTime =
      sameDayBackups.length > 0 ? sameDayBackups.at(-1)!.dateTime.toMillis() : backupTimestamp.toMillis();

    const olderBackups = backupsWithDateTimes
      .filter(b => b.dateTime.toMillis() < cutoffTime)
      .sort((a, b) => b.dateTime.toMillis() - a.dateTime.toMillis());

    const hasShowMore = sameDayBackups.length > 0 && olderBackups.length > 0;
    if (hasShowMore) {
      options.push(SHOW_MORE_OPTION);
      if (selectedBackupState.showMore) {
        options.push(...olderBackups.map(b => b.backup.name).slice(0, 3));
      }
    } else if (sameDayBackups.length === 0 && olderBackups.length > 0) {
      options.push(...olderBackups.map(b => b.backup.name).slice(0, 3));
    }

    return options;
  }, [backupTimestamp, continuousBackups, selectedBackupState.showMore]);

  const onClose = () => {
    onCancel();
    setSelectedBackupState(initialSelectedBackupState);
    resetForm();
  };

  const renderAutocompleteInput = (renderParams: AutocompleteRenderInputParams) => (
    <OutlinedInput
      {...destructRenderInputParams(renderParams)}
      id="continuousBackup"
      name="continuousBackup"
      label={t('cluster:details.header.restore_form.backup_label')}
      aria-disabled={!continuousBackupsOptions.length}
    />
  );

  const handleBackupChange = useCallback(
    (event: SyntheticEvent<Element, Event>, value: string) => {
      if (value === SHOW_MORE_OPTION) {
        event.preventDefault();
        event.stopPropagation();
      } else {
        void setFieldValue('backupName', value);
        setSelectedBackupState({ isSelected: !!value });
      }
    },
    [setFieldValue, setSelectedBackupState],
  );

  const renderBackupOption = (props: React.HTMLAttributes<HTMLLIElement> & { key: any }, value: string) => {
    const { key, ...otherProps } = props;
    if (value === SHOW_MORE_OPTION) {
      return (
        <li
          {...otherProps}
          key={key}
          onClick={e => {
            e.stopPropagation();
            setSelectedBackupState(state => ({ ...state, showMore: !state.showMore }));
          }}
        >
          <Typography sx={{ color: CIColors.Tertiary, display: 'flex', alignItems: 'center', gap: '10px' }}>
            {selectedBackupState.showMore ? <VisibilityOffIcon /> : <VisibilityIcon />}
            {selectedBackupState.showMore
              ? t('cluster:details.header.restore_form.show_less_options')
              : t('cluster:details.header.restore_form.show_more_options')}
          </Typography>
        </li>
      );
    }

    const continuousBackup = continuousBackups.find(b => b.name === value);
    return continuousBackup ? (
      <li {...otherProps} key={key}>
        <div style={{ display: 'flex', flexFlow: 'column' }}>
          <b>{continuousBackup.name}</b>
          <Typography variant="caption" sx={{ color: '#909196', fontSize: '12px' }}>
            {`${humanize.fileSize(continuousBackup.sizeInBytes)} | ${DateTime.fromISO(continuousBackup.lastModified, { zone: 'utc' }).toFormat(dateFormat)}`}
          </Typography>
        </div>
      </li>
    ) : null;
  };

  const backupDateTimeLocal = useMemo(() => {
    const localTime = backupTimestamp && backupTimestamp.toLocal();
    return localTime && localTime.isValid ? `${localTime.toFormat(dateFormat)} (${utcLocal})` : null;
  }, [backupTimestamp, utcLocal, dateFormat]);

  return (
    <HUModal2
      open={open}
      onOk={submitForm}
      okButtonProps={{ disabled: isFetching }}
      okButtonText={t('cluster:details.header.restore_form.restore_button')}
      onCancel={onClose}
      cancelButtonProps={{ disabled: isFetching }}
      onClose={onClose}
      size="big"
      header={({ headerClass, titleClass }) => (
        <div className={headerClass}>
          <Typography classes={{ h2: titleClass }} variant="h2">
            {t('cluster:details.header.restore_form.title')}
          </Typography>
          <Typography variant="h4" align="center">
            {t('cluster:details.header.restore_form.tooltip')}
          </Typography>
        </div>
      )}
    >
      <>
        <div className={classes.formControlWithLabel}>
          <div className={cx(classes.formControl, classes.widthDefault, classes.datePickerField)}>
            <DateTimePicker
              ampm={false}
              label={t('createCluster:labels.backup_datetime')}
              value={backupTimestamp}
              format={dateFormat}
              onChange={value => handleBackupDateTimeChange(value)}
              minDateTime={backupMinTimestamp}
              maxDateTime={backupMaxTimestamp}
              slotProps={{
                dialog: { className: mpClasses.popoverRoot },
                textField: {
                  inputProps: {
                    'data-cy': 'restore-cluster-dialog-datetime-input',
                  },
                },
              }}
              disabled={isFetching || !oldestRestorePoint || selectedBackupState.isSelected}
            />
            {hasError('backupTimestamp') && (
              <FormHelperText error>{getError('backupTimestamp', backupTimestampErrorOptions)}</FormHelperText>
            )}
          </div>
          <Typography
            className={cx({
              [classes.rightFormLabel]: true,
              [restoreClasses.tooltipDisabled]: selectedBackupState.isSelected,
            })}
            fontSize="14px"
          >
            {t('createCluster:labels.local_time')}:
            <br />
            {backupDateTimeLocal}
          </Typography>
        </div>
        <div className={classes.formControlWithLabel}>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthDefault, classes.datePickerField)}
            variant="outlined"
            error={hasError('backupName')}
          >
            <InputLabel htmlFor="backup">{t('cluster:details.header.restore_form.backup_label')}</InputLabel>
            <HUAutocomplete
              renderInput={renderAutocompleteInput}
              popupIcon={<ArrowRoundedIcon />}
              options={continuousBackupsOptions}
              getOptionLabel={opt => (opt === SHOW_MORE_OPTION ? '' : opt)}
              value={values.backupName}
              onChange={(e, value) => handleBackupChange(e, value || '')}
              renderOption={renderBackupOption}
              disabled={isFetching}
              loading={clusterContinuousBackupsQuery.isLoading}
              data-cy="restore-cluster-dialog-backup-select"
            />
            {hasError('backupName') && <FormHelperText error>{getError('backupName')}</FormHelperText>}
            <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>
              {t('cluster:details.header.restore_form.backup_tooltip')}
            </FormHelperText>
          </FormControl>
          <div className={classes.rightFormLabel}>
            <Fade in={selectedBackupState.isSelected}>
              <Typography fontSize="14px" data-cy="restore-cluster-dialog-backup-local-time">
                {t('createCluster:labels.local_time')}:
                <br />
                {selectedBackupState.localTimestamp}
              </Typography>
            </Fade>
          </div>
        </div>
        <Alert severity="warning" sx={{ marginTop: '20px' }} classes={{ icon: restoreClasses.icon }}>
          <Typography style={{ fontSize: '14px' }}>
            {t('cluster:details.header.restore_form.warning_message')}
          </Typography>
        </Alert>
      </>
    </HUModal2>
  );
});
